import { memo, useMemo } from 'react'
import type { AnchorSide, Relation, Table } from '@/types'
import { calcPath } from '@/utils/paths'
import { getTableBounds, getTableHeight, getTableWidth, type Point } from '@/utils/geometry'
import { useAnchorDrag } from '@/hooks/useAnchorDrag'
import { useHandleDrag } from '@/hooks/useHandleDrag'

interface RelationHandlesProps {
  relationships: Relation[]
  tables: Table[]
  selectedRelationshipId: string | null
  readOnly?: boolean
}

function offsetHandlePoint(x: number, y: number, side: AnchorSide, distance = 10) {
  switch (side) {
    case 'left':
      return { x: x - distance, y }
    case 'right':
      return { x: x + distance, y }
    case 'top':
      return { x, y: y - distance }
    case 'bottom':
      return { x, y: y + distance }
  }
}

export const RelationHandles = memo(function RelationHandles({
  relationships,
  tables,
  selectedRelationshipId,
  readOnly = false,
}: RelationHandlesProps) {
  const startAnchorDrag = useAnchorDrag()
  const startHandleDrag = useHandleDrag()

  const tablesById = useMemo(
    () => new Map(tables.map((table) => [table.id, table])),
    [tables],
  )

  const selectedRelation = relationships.find((r) => r.id === selectedRelationshipId)
  if (!selectedRelation || readOnly) return null

  const fromTable = tablesById.get(selectedRelation.from)
  const toTable   = tablesById.get(selectedRelation.to)
  if (!fromTable || !toTable) return null

  const path = calcPath(selectedRelation, fromTable, toTable, tables)

  const fromRect = getTableBounds(fromTable)
  const toRect   = getTableBounds(toTable)

  const canvasWidth  = tables.reduce((v, t) => Math.max(v, t.x + getTableWidth(t) + 200), 2000)
  const canvasHeight = tables.reduce((v, t) => Math.max(v, t.y + getTableHeight(t) + 200), 2000)

  const fromHandle = offsetHandlePoint(path.fromPoint.x, path.fromPoint.y, path.fromSide)
  const toHandle   = offsetHandlePoint(path.toPoint.x,   path.toPoint.y,   path.toSide)

  // Interior bend points — exclude first (fromAnchor) and last (toAnchor)
  const interiorPoints: Point[] = path.points.slice(1, -1)

  // Waypoints to pass to drag hook — if already manual, use stored; otherwise use computed
  const currentWaypoints: Point[] = (selectedRelation.isManual && selectedRelation.waypoints?.length)
    ? selectedRelation.waypoints
    : interiorPoints

  // Computed anchors — used to lock endpoints when turning a route manual for the first time
  const computedFromAnchor = { side: path.fromPoint.side, offsetPercent: path.fromPoint.offsetPercent }
  const computedToAnchor   = { side: path.toPoint.side,   offsetPercent: path.toPoint.offsetPercent }

  return (
    <svg
      id="svgh"
      width={canvasWidth}
      height={canvasHeight}
      style={{ position: 'absolute', inset: 0, overflow: 'visible', zIndex: 12 }}
    >
      {/* Anchor handles (green) — slide along the table edge */}
      <circle
        cx={fromHandle.x}
        cy={fromHandle.y}
        r={7}
        fill="#10B981"
        stroke="#FFFFFF"
        strokeWidth={2}
        className="wp-handle"
        data-relation-editor-affordance="true"
        onPointerDown={(e) => startAnchorDrag(e, selectedRelation.id, 'from', fromTable)}
      />
      <circle
        cx={toHandle.x}
        cy={toHandle.y}
        r={7}
        fill="#10B981"
        stroke="#FFFFFF"
        strokeWidth={2}
        className="wp-handle"
        data-relation-editor-affordance="true"
        onPointerDown={(e) => startAnchorDrag(e, selectedRelation.id, 'to', toTable)}
      />

      {/* Bend handles (blue) — one per interior inflection, free 2D movement */}
      {interiorPoints.map((point, i) => (
        <circle
          key={i}
          cx={point.x}
          cy={point.y}
          r={5}
          fill="#4F46E5"
          stroke="#FFFFFF"
          strokeWidth={2}
          className="rel-handle"
          data-relation-editor-affordance="true"
          style={{ cursor: 'move' }}
          onPointerDown={(e) =>
            startHandleDrag(
              e,
              selectedRelation.id,
              i,
              currentWaypoints,
              computedFromAnchor,
              computedToAnchor,
              path.fromPoint,
              path.toPoint,
              fromRect,
              toRect,
            )
          }
        />
      ))}
    </svg>
  )
})
